import { serviceOrigin } from './services';
// Avatar-Manifest v2 — die Video-State-Machine von Arcturian.
//
// Quelle: `agentos.avatar-manifest.v2`, geprüfte Entwicklungsvariante
// https://api-storage.arkturian.com/storage/media/117159 (SHA-256 des Manifests
// gegen AppDevs Angabe verifiziert: 3d174899…3a9436).
//
// Das Manifest ist NICHT nur eine Asset-Liste: es pinnt Rollen, Checksummen und
// Rendererprofile — inklusive eines eigenen `web`-Profils (Crossfade 100 ms,
// linear, 1 Decoder im Ruhezustand, 2 im Übergang). Der Browser hält sich daran,
// statt eigene Werte zu erfinden; sonst sieht der Avatar auf jeder Plattform
// anders aus, obwohl es dieselbe Figur sein soll.
//
// Persona, Voice und Authority sind GETRENNTE revisionierte Verträge. Das
// Manifest ist ausdrücklich keine Tool-Erlaubnis — hier wird nur gezeigt, nie
// gehandelt.

export const arcturianManifestUrl = () => { const o = serviceOrigin('storage'); return o ? `${o}/storage/media/117159` : null; };

/** Worauf wir uns festlegen. Weicht das geladene Manifest ab, wird es
 *  verworfen statt „irgendwie" gerendert — eine fremde Figur oder ein anderes
 *  Schema ist ein Fehler, kein Zufallsfund. */
export const PINNED = {
  schema: 'agentos.avatar-manifest.v2',
  characterId: 'arcturian.cosmic-guide',
  variantId: 'portrait',
} as const;

/** Die zehn Rollen aus dem Manifest. `greeting` ist die einzige, die NICHT
 *  loopt — sie läuft einmal und übergibt an `idle`. */
export type AvatarRole =
  | 'greeting' | 'idle' | 'listening' | 'thinking'
  | 'talk_short' | 'talk_medium' | 'talk_long'
  | 'happy' | 'joyful' | 'sad';

export interface AvatarAsset {
  role: string;
  url: string;
  poster?: string;
  loop?: boolean;
  duration?: number;
  sha256?: string;
  width?: number;
  height?: number;
}

export interface RendererProfile {
  crossfade_ms?: number;
  easing?: string;
  steady_state_decoders?: number;
  transition_decoders?: number;
}

export interface AvatarManifest {
  schema?: string;
  manifest_revision?: string;
  character_id?: string;
  variant_id?: string;
  canonical_frame?: { url?: string; poster?: string };
  assets?: Record<string, AvatarAsset>;
  renderer_profiles?: Record<string, RendererProfile>;
}

export type ManifestResult =
  | { ok: true; manifest: AvatarManifest; profile: Required<RendererProfile> }
  | { ok: false; reason: string };

// Fallback NUR für die Darstellungsparameter, nicht für Assets: fehlt das
// web-Profil, rendern wir trotzdem — aber niemals mit erfundenen Videos.
const WEB_DEFAULTS: Required<RendererProfile> = {
  crossfade_ms: 100,
  easing: 'linear',
  steady_state_decoders: 1,
  transition_decoders: 2,
};

/** Prüft ein geladenes Manifest gegen die Pins. Fail-closed. */
export function verifyManifest(m: AvatarManifest | null | undefined): ManifestResult {
  if (!m || typeof m !== 'object') return { ok: false, reason: 'kein Manifest' };
  if (m.schema !== PINNED.schema) return { ok: false, reason: `Schema ${String(m.schema)} ≠ ${PINNED.schema}` };
  if (m.character_id !== PINNED.characterId) return { ok: false, reason: `character_id ${String(m.character_id)}` };
  if (m.variant_id !== PINNED.variantId) return { ok: false, reason: `variant_id ${String(m.variant_id)}` };
  const assets = m.assets;
  if (!assets || typeof assets !== 'object') return { ok: false, reason: 'assets fehlen' };
  // Ohne idle gibt es keinen Ruhezustand — dann lieber gar nicht anzeigen als
  // ein Standbild, das wie ein eingefrorener Avatar aussieht.
  if (!assets.idle?.url) return { ok: false, reason: 'Rolle idle fehlt' };
  const profile = { ...WEB_DEFAULTS, ...(m.renderer_profiles?.web || {}) };
  return { ok: true, manifest: m, profile };
}

export async function loadManifest(url = arcturianManifestUrl()): Promise<ManifestResult> {
  if (!url) return { ok: false, reason: 'storage_unavailable' };
  try {
    const res = await fetch(url);
    if (!res.ok) return { ok: false, reason: `HTTP ${res.status}` };
    return verifyManifest(await res.json());
  } catch (e) {
    return { ok: false, reason: e instanceof Error ? e.message : 'Netzwerkfehler' };
  }
}

/**
 * Welcher Clip gehört zu einem Zustand — mit Rückfall auf `idle`.
 *
 * Die Sprech-Rollen sind nach Länge gestaffelt (short/medium/long). Welche
 * passt, hängt daran, wie lange die Antwort dauert; das weiß erst der Player.
 * Diese Funktion trifft die Wahl deshalb NICHT selbst, sondern nimmt die Rolle
 * entgegen — sonst läge eine Timing-Heuristik in einer Asset-Lookup-Funktion.
 */
export function assetFor(m: AvatarManifest, role: AvatarRole): AvatarAsset | null {
  return m.assets?.[role] || m.assets?.idle || null;
}

/** Sprech-Clip nach erwarteter Dauer. Getrennt von `assetFor`, damit die Regel
 *  sichtbar und testbar ist statt im Player vergraben. */
export function talkRoleFor(seconds: number): AvatarRole {
  if (seconds <= 5) return 'talk_short';
  if (seconds <= 9) return 'talk_medium';
  return 'talk_long';
}
